/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	type ForwardRefExoticComponent,
	type Ref,
	type RefAttributes,
	type RefObject,
	useCallback,
	useId,
	useMemo,
} from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { useRouter, useRouterActions } from 'react-resource-router-next';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap, cx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditorSearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import Textfield from '@atlaskit/textfield/text-field';
import type { TextfieldProps as TextFieldProps } from '@atlaskit/textfield/types';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { SquareReturnIcon } from '@atlassian/search-dialog/square-return-icon';

import { messages } from './messages';

const cssOverrideWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/design-system/no-nested-styles
	'&& [data-ds--text-field--container]': {
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
	},
});

const styles = cssMap({
	wrapper: {
		width: '400px',
	},
	responsiveWrapper: {
		maxWidth: '100%',
	},
	searchIcon: {
		minWidth: 'fit-content',
		minHeight: 'fit-content',
		marginLeft: token('space.100'),
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	clearIcon: {
		marginRight: token('space.100'),
	},
	searchEnterHint: {
		marginRight: token('space.150'),
		flexShrink: 0,
		whiteSpace: 'nowrap',
		display: 'flex',
		alignItems: 'center',
	},
	searchEnterHintIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		transform: 'scale(0.833333)',
	},
});

export type SearchTextFieldProps = {
	isReadOnly: boolean;
	keyboardRef: RefObject<HTMLInputElement>;
	onInput(value: string): void;
	placeholderText?: string;
	queryWord: string;
	searchBasePath?: string;
	showDropdown: boolean;
	showSearchEnterHint?: boolean;
};

export const SearchTextField: ForwardRefExoticComponent<
	SearchTextFieldProps & RefAttributes<HTMLDivElement>
> = forwardRef((props: SearchTextFieldProps, ref: Ref<HTMLDivElement>) => {
	const {
		isReadOnly,
		onInput,
		placeholderText,
		queryWord,
		searchBasePath,
		showSearchEnterHint = false,
	} = props;

	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const [routerState] = useRouter();
	const searchEnterHintDescriptionId = useId();

	const handleChange: TextFieldProps['onChange'] = (e) => {
		const value = e.currentTarget.value;
		onInput(value);
	};

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();

				if (showSearchEnterHint && queryWord.trim().length === 0) {
					return;
				}

				const currentPath = searchBasePath || routerState.location.pathname;
				const updatedPathIfHasMissingTrailingSlash = currentPath.endsWith('/')
					? currentPath
					: `${currentPath}/`;
				push(
					`${updatedPathIfHasMissingTrailingSlash}articles/search?q=${encodeURIComponent(
						queryWord,
					)}`,
				);
			}
		},
		[searchBasePath, routerState?.location?.pathname, queryWord, push, showSearchEnterHint],
	);

	const handleClear = useCallback(() => {
		onInput('');
	}, [onInput]);

	const isHintVisible = showSearchEnterHint && queryWord.trim().length > 0;

	const trailingElement = useMemo(() => {
		if (isHintVisible) {
			return (
				<Box xcss={styles.searchEnterHint} testId="search-enter-hint">
					<VisuallyHidden>
						<Text id={searchEnterHintDescriptionId}>
							{formatMessage(messages.searchHintDescription)}
						</Text>
					</VisuallyHidden>
					<Inline alignBlock="center" space="space.100">
						<Text color="color.text.subtle" size="small">
							{formatMessage(messages.searchHint)}
						</Text>
						<span aria-hidden="true" css={styles.searchEnterHintIcon}>
							<SquareReturnIcon />
						</span>
					</Inline>
				</Box>
			);
		}

		if (queryWord) {
			return (
				<Box xcss={styles.clearIcon}>
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} color="currentColor" label="" />}
						spacing="compact"
						onClick={handleClear}
						label=""
						appearance="subtle"
					/>
				</Box>
			);
		}

		return null;
	}, [formatMessage, handleClear, isHintVisible, queryWord, searchEnterHintDescriptionId]);

	return (
		<Box
			ref={ref}
			xcss={cx(styles.wrapper, fg('hubs_jsm_max_width_appearance') && styles.responsiveWrapper)}
		>
			<div css={cssOverrideWrapperStyles}>
				<Textfield
					ref={props.keyboardRef}
					id="article-search-query-input"
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					isReadOnly={isReadOnly}
					placeholder={placeholderText ?? formatMessage(messages.defaultPlaceholder)}
					value={queryWord}
					aria-describedby={isHintVisible ? searchEnterHintDescriptionId : undefined}
					elemBeforeInput={
						<Box xcss={styles.searchIcon}>
							<EditorSearchIcon color="currentColor" spacing="spacious" label="" />
						</Box>
					}
					elemAfterInput={trailingElement}
				/>
			</div>
		</Box>
	);
});
